import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import adyantamLogo from "../assets/adyantam-logo.png";

const styles = {
  page: {
    minHeight: "100vh",
    background: "#0f0f13",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    color: "#f3f4f6",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: "440px",
    background: "#161821",
    border: "1px solid #2b2f3d",
    borderRadius: "18px",
    padding: "28px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
  },
  brand: {
    textAlign: "center",
    marginBottom: "22px",
  },
  brandLogo: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    objectFit: "cover",
    marginBottom: "10px",
  },
  brandName: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#8b7cff",
    letterSpacing: "1px",
  },
  brandSub: {
    fontSize: "13px",
    color: "#8f95a8",
    marginTop: "6px",
  },
  title: {
    fontSize: "20px",
    fontWeight: 600,
    marginBottom: "6px",
  },
  subtitle: {
    fontSize: "13px",
    color: "#8f95a8",
    marginBottom: "20px",
  },
  field: {
    marginBottom: "14px",
  },
  label: {
    display: "block",
    fontSize: "12px",
    color: "#8f95a8",
    marginBottom: "6px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  input: {
    width: "100%",
    border: "1px solid #2b2f3d",
    background: "#0f0f13",
    color: "#f3f4f6",
    borderRadius: "10px",
    padding: "12px 14px",
    fontSize: "14px",
    boxSizing: "border-box",
    outline: "none",
  },
  passwordWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  passwordToggle: {
    position: "absolute",
    right: "10px",
    background: "transparent",
    border: "none",
    color: "#8f95a8",
    cursor: "pointer",
    fontSize: "13px",
  },
  button: {
    width: "100%",
    border: "none",
    borderRadius: "10px",
    padding: "12px 14px",
    background: "linear-gradient(135deg, #6c63ff, #8b7cff)",
    color: "#fff",
    fontSize: "15px",
    fontWeight: 600,
    cursor: "pointer",
    marginTop: "8px",
  },
  buttonDisabled: {
    opacity: 0.7,
    cursor: "wait",
  },
  error: {
    marginTop: "12px",
    padding: "10px 12px",
    borderRadius: "10px",
    background: "rgba(255, 82, 82, 0.14)",
    border: "1px solid rgba(255, 82, 82, 0.3)",
    color: "#ff7b7b",
    fontSize: "13px",
  },
  footer: {
    marginTop: "18px",
    textAlign: "center",
    fontSize: "12px",
    color: "#6b7280",
  },
  linkButton: {
    background: "transparent",
    border: "none",
    color: "#8b7cff",
    cursor: "pointer",
    fontSize: "13px",
    padding: 0,
    marginTop: "6px",
  },
  success: {
    marginTop: "12px",
    padding: "10px 12px",
    borderRadius: "10px",
    background: "rgba(62, 207, 142, 0.16)",
    border: "1px solid rgba(62, 207, 142, 0.28)",
    color: "#7ce0ad",
    fontSize: "13px",
  },
  spinner: {
    display: "inline-block",
    width: "14px",
    height: "14px",
    border: "2px solid rgba(255,255,255,0.25)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    marginRight: "8px",
    animation: "spin 0.8s linear infinite",
  },
};

export default function LoginScreen() {
  const { login, forgotPassword, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);

    const result = await login(email, password);
    if (!result.success) {
      setError(result.error || "Unable to sign in.");
    }

    setSubmitting(false);
  }

  async function handleForgotPassword() {
    if (!email.trim()) {
      setError("Please enter your email address first.");
      return;
    }

    const result = await forgotPassword(email);
    if (result.success) {
      setSuccess("Password reset email sent. Please check your inbox.");
      setError("");
    } else {
      setError(result.error || "Unable to send reset email.");
      setSuccess("");
    }
  }

  return (
    <>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.brand}>
          <img src={adyantamLogo} alt="Adyantam" style={styles.brandLogo} />
          <div style={styles.brandName}>Adyantam Business Consultants</div>
          <div style={styles.brandSub}>Admin dashboard for backend operations</div>
        </div>

        <div style={styles.title}>Welcome back</div>
        <div style={styles.subtitle}>Sign in with your platform email and password.</div>

        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="login-email">Email</label>
            <input
              id="login-email"
              style={styles.input}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              autoComplete="email"
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="login-password">Password</label>
            <div style={styles.passwordWrap}>
              <input
                id="login-password"
                style={{ ...styles.input, paddingRight: "46px" }}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
              <button
                type="button"
                style={styles.passwordToggle}
                onClick={() => setShowPassword((value) => !value)}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            <button type="button" style={styles.linkButton} onClick={handleForgotPassword}>
              Forgot Password?
            </button>
          </div>

          <button
            style={{ ...styles.button, ...(submitting || loading ? styles.buttonDisabled : {}) }}
            type="submit"
            disabled={submitting || loading}
          >
            {submitting || loading ? (
              <><span style={styles.spinner} /> Signing in...</>
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        {error ? <div style={styles.error}>{error}</div> : null}
        {success ? <div style={styles.success}>{success}</div> : null}

        <div style={styles.footer}>Only active platform users can access the dashboard.</div>
      </div>
      </div>
    </>
  );
}
